// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useState } from "react"
import { Link } from "react-router-dom"
import { Plus, KeyRound } from "lucide-react"
import { LeftNav } from "./component.leftNav"
import { formatCurrency } from "@/utils"
import { useAlertModal } from "@/hooks/useAlertModal"
import { ApiKeyCreatedAlert } from "./component.apiKeyCreatedAlert"
import { useChangelog } from "@/hooks/useChangelog"
import { ChangelogModal } from "@/views/home/component.changelogModal"
import SmartTable from "@/components/ui/SmartTable"
import { apiKeyColumns } from "./apiKeys.columns"
import {
  useUserSelf,
  useRedeemCode,
  getRedeemErrorMessage,
} from "@/queries/queries.users"
import {
  useApiKeysSelf,
  useCreateApiKey,
  useDeleteApiKey,
  type ApiKey,
} from "@/queries/queries.apikeys"

export const AuthenticatedLayout = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { showChangelog, currentMessage, handleMarkAsSeen, handleClose } =
    useChangelog()

  return (
    <div className="flex h-full w-full">
      <LeftNav />
      <main className="flex-1 min-w-0 flex flex-col gap-4 px-7.5 py-7 overflow-auto [scrollbar-width:none]">
        {children}
      </main>
      <AlertModal />
      {showChangelog && currentMessage && (
        <ChangelogModal
          message={currentMessage}
          onMarkAsSeen={handleMarkAsSeen}
          onClose={handleClose}
        />
      )}
    </div>
  )
}

/**
 * Lists the current user's API keys — masked value, creation and
 * expiration dates, a discreet "expired" marker when past — newest
 * first, through the shared Lagon `SmartTable`. A muted message renders
 * when the user has no keys yet. Only the server-masked `key` is ever
 * rendered here; the full secret is shown exactly once, in the creation
 * alert.
 */
const ApiKeysList = ({
  onRevoke,
}: {
  onRevoke: (key: ApiKey) => void
}) => {
  const { data: apiKeys } = useApiKeysSelf()

  // Nothing has arrived yet — render nothing rather than falsely
  // claiming the user has no keys (the empty state below is reserved
  // for a confirmed empty list).
  if (!apiKeys) return null

  if (apiKeys.length === 0) {
    return (
      <div className="box-dashed flex flex-col items-center justify-center gap-3 py-11 text-center">
        <div className="flex h-[46px] w-[46px] items-center justify-center rounded-[13px] border border-dashed border-border-active text-border-active">
          <KeyRound size={22} strokeWidth={1.8} />
        </div>
        <span className="text-[13.5px] text-text-secondary">No API keys yet.</span>
      </div>
    )
  }

  const sorted = [...apiKeys].sort(
    (a, b) =>
      new Date(b.createdAt ?? 0).getTime() -
      new Date(a.createdAt ?? 0).getTime(),
  )

  return <SmartTable data={sorted} columns={apiKeyColumns(onRevoke)} />
}

/** Input + button + outcome message for redeeming a beta-invite code. */
const RedeemCodeSection = () => {
  const [code, setCode] = useState("")
  const redeemMutation = useRedeemCode()

  const handleRedeem = () => {
    const trimmed = code.trim()
    if (!trimmed) return

    redeemMutation.mutate(trimmed)
  }

  const outcome = redeemMutation.isSuccess
    ? {
        kind: "success" as const,
        text: "Code redeemed — your credit balance has been updated.",
      }
    : redeemMutation.isError
      ? {
          kind: "error" as const,
          text: getRedeemErrorMessage(redeemMutation.error),
        }
      : null

  return (
    <section className="box p-6 lg:col-span-12">
      <div className="mb-4 font-mono text-[11px] uppercase tracking-[0.12em] text-text-tertiary">
        Redeem a code
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        <input
          aria-label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Enter your code"
          className="h-9 min-w-[220px] flex-1 rounded-[10px] border border-border-strong bg-[rgba(4,20,24,0.5)] px-3 text-[12.5px] text-foreground placeholder:text-text-tertiary"
        />
        <button
          onClick={handleRedeem}
          disabled={code.trim() === "" || redeemMutation.isPending}
          className="inline-flex h-9 items-center gap-2 rounded-[10px] border border-primary/40 bg-primary/12 px-[15px] text-[12.5px] font-semibold text-primary transition-shadow hover:shadow-glow-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          Redeem
        </button>
      </div>
      {outcome && (
        <div
          role={outcome.kind === "error" ? "alert" : "status"}
          className="mt-3 text-[12.5px] text-text-secondary"
        >
          {outcome.text}
        </div>
      )}
    </section>
  )
}

export const AccountView = () => {
  const { data: userData } = useUserSelf()
  const { pushAlertModal, shiftAlertModal } = useAlertModal()

  const createApiKeyMutation = useCreateApiKey()
  const deleteApiKeyMutation = useDeleteApiKey()

  const handleCreateApiKey = () => {
    createApiKeyMutation.mutateAsync().then((data) => {
      pushAlertModal({
        title: "API Key Created",
        text: <ApiKeyCreatedAlert apiKey={data.key} />,
      })
    })
  }

  const handleRevokeApiKey = (key: ApiKey) => {
    if (!key.uuid) return
    pushAlertModal({
      title: "Revoke API Key",
      text: (
        <div className="flex flex-col gap-2">
          <span>This will permanently revoke the following key:</span>
          <span className="font-mono text-sm select-text">{key.key}</span>
          <span>Scripts using it will stop working. This cannot be undone.</span>
        </div>
      ),
      actions: [
        <button
          key="cancel"
          onClick={() => shiftAlertModal()}
          className="border border-border-strong text-text-secondary text-[12.5px] px-3 py-1.5 rounded-[9px] cursor-pointer transition-colors hover:border-primary hover:text-foreground"
        >
          Cancel
        </button>,
        <button
          key="confirm"
          onClick={() => {
            // Close only once the server has answered: on success (or
            // already-gone) the row vanishes via invalidation; on
            // failure the key is still live — say so.
            deleteApiKeyMutation
              .mutateAsync(key.uuid!)
              .then(() => shiftAlertModal())
              .catch(() => {
                shiftAlertModal()
                pushAlertModal({
                  title: "Revoke failed",
                  text: (
                    <div className="flex flex-col gap-2">
                      <span>The following key could not be revoked:</span>
                      <span className="font-mono text-sm select-text">
                        {key.key}
                      </span>
                      <span>It is still active — please try again.</span>
                    </div>
                  ),
                })
              })
          }}
          className="inline-flex items-center gap-1.5 bg-destructive border-none text-[#2a0b08] text-[12.5px] font-semibold px-3 py-1.5 rounded-[9px] cursor-pointer transition-opacity opacity-90 hover:opacity-100"
        >
          Revoke key
        </button>,
      ],
    })
  }

  const email = userData?.email ?? ""
  const initials = (email.slice(0, 2) || "··").toUpperCase()
  const github = userData?.github_username

  // Credit health is derived client-side from the remaining balance —
  // no backend "state" field exists. Thresholds: <=0 empty, <10 low.
  const credits = userData?.credits ?? 0
  const credState =
    credits <= 0 ? "empty" : credits < 10 ? "low" : "healthy"
  const CRED = {
    healthy: { color: "#8FE07F", glow: "rgba(104,204,88,0.3)", bg: "rgba(104,204,88,0.12)", border: "rgba(104,204,88,0.4)" },
    low: { color: "#F4BE72", glow: "rgba(242,169,74,0.3)", bg: "rgba(242,169,74,0.12)", border: "rgba(242,169,74,0.4)" },
    empty: { color: "#FF8A7E", glow: "rgba(255,107,107,0.3)", bg: "rgba(255,107,107,0.12)", border: "rgba(255,107,107,0.4)" },
  }[credState]

  return (
    <div className="flex flex-col gap-6">
      {/* page header */}
      <div className="flex flex-col gap-[7px] border-b border-border pb-5">
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-text-tertiary">
          Settings · account
        </span>
        <h1 className="text-[26px] font-semibold tracking-[-0.015em] text-foreground">
          My account
        </h1>
      </div>

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-12">
        {/* ===== Identity (read-only — no profile-edit endpoint) ===== */}
        <section className="box self-stretch p-6 lg:col-span-7">
          <div className="mb-5 font-mono text-[11px] uppercase tracking-[0.12em] text-text-tertiary">
            Identity
          </div>
          <div className="mb-5 flex items-center gap-4">
            <div
              className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[16px] text-[22px] font-semibold text-[#04141A]"
              style={{
                background: "linear-gradient(135deg, #45F0C8, #16C2A0)",
                boxShadow: "0 0 22px rgba(69,240,200,0.32)",
              }}
            >
              {initials}
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <span className="truncate text-[19px] font-semibold tracking-[-0.01em] text-foreground">
                {email}
              </span>
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                {userData?.role && (
                  <span className="inline-flex items-center rounded-full border border-border-strong px-2.5 py-[3px] font-mono uppercase tracking-[0.04em] text-text-secondary">
                    {userData.role}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full border border-lineage/40 bg-lineage/10 px-2.5 py-[3px] text-lineage">
                  {github ? `Connected via GitHub` : "Email & password"}
                </span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <DetailField label="Email" value={email || "—"} />
            <DetailField
              label="Connection"
              value={github ? `@${github}` : "Email & password"}
            />
          </div>
        </section>

        {/* ===== Credits ===== */}
        <section className="box flex flex-col self-stretch p-6 lg:col-span-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-text-tertiary">
              Credits
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[11px]"
              style={{ background: CRED.bg, color: CRED.color, border: `1px solid ${CRED.border}` }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: CRED.color }} />
              {credState}
            </span>
          </div>
          <div className="mb-1.5 flex flex-wrap items-baseline gap-2">
            <span
              className="whitespace-nowrap text-[34px] font-semibold tracking-[-0.02em]"
              style={{ color: CRED.color, textShadow: `0 0 18px ${CRED.glow}` }}
            >
              {formatCurrency(credits, "USD")}
            </span>
            <span className="text-[14px] text-text-tertiary">credits remaining</span>
          </div>
          <Link
            to="/request-credits"
            className="mt-auto inline-flex h-[42px] items-center justify-center rounded-[11px] text-[13.5px] font-semibold no-underline transition-shadow"
            style={{
              background: "linear-gradient(135deg, #45F0C8, #16C2A0)",
              color: "#04141A",
              boxShadow: "0 0 16px rgba(69,240,200,0.28)",
            }}
          >
            Request more credits
          </Link>
        </section>

        {/* ===== API keys ===== */}
        <section className="box p-6 lg:col-span-12">
          <div className="mb-4 flex items-center justify-between gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-text-tertiary">
              API keys
            </span>
            <button
              onClick={() => handleCreateApiKey()}
              className="inline-flex h-9 items-center gap-2 rounded-[10px] border border-primary/40 bg-primary/12 px-[15px] text-[12.5px] font-semibold text-primary transition-shadow hover:shadow-glow-sm"
            >
              <Plus size={14} strokeWidth={2.4} />
              New key
            </button>
          </div>
          <ApiKeysList onRevoke={handleRevokeApiKey} />
        </section>

        <RedeemCodeSection />
      </div>
    </div>
  )
}

/** A read-only labelled identity field (mockup §Identity read mode). */
const DetailField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1.5 rounded-[11px] border border-[#0E2E33] bg-[rgba(4,20,24,0.5)] px-3.5 py-3">
    <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-text-tertiary">
      {label}
    </span>
    <span className="truncate text-[14px] text-foreground">{value}</span>
  </div>
)

export const AlertModal = () => {
  const { alertModals, shiftAlertModal } = useAlertModal()

  if (alertModals.length === 0) {
    return null
  }

  const { title, text, onClose, actions } = alertModals[0]

  return (
    <div className="fixed top-0 left-0 w-full h-full bg-black/80 flex items-center justify-center z-1000">
      <div
        role="dialog"
        className="bg-popover p-7 rounded-[18px] border border-border-strong shadow-popover max-w-[42em] w-[90%] flex flex-col items-start justify-center gap-4 animate-box-in"
      >
        <div className="flex flex-row items-center justify-between mb-1">
          <div className="text-[17px] font-semibold m-0 text-foreground">
            {title}
          </div>
        </div>
        <div className="text-text-secondary text-[13.5px] whitespace-pre-wrap [user-select:text]">
          {text}
        </div>
        <div className="flex flex-row gap-2 justify-end items-center w-full">
          {actions && Array.isArray(actions) && actions.length > 0 ? (
            actions.map((action, idx) => <span key={idx}>{action}</span>)
          ) : (
            <button
              onClick={() => {
                shiftAlertModal()
                onClose?.()
              }}
              className="border border-border-strong text-text-secondary text-[12.5px] px-3 py-1.5 rounded-[9px] cursor-pointer transition-colors hover:border-primary hover:text-foreground"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
